<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            360 real estate photography has moved from luxury service to
            standard expectation in competitive property markets. Agents who
            master it win more listings, sell properties faster, and command
            higher commissions. This complete guide covers everything —
            equipment, shooting technique, room-by-room strategy, common errors,
            and how to deliver finished tours that impress both sellers and
            buyers.
          </p>
        </section>

        <section>
          <h2>Why 360 Photography Is Now Essential in Real Estate</h2>

          <h3>The Buyer Behaviour Shift</h3>
          <p>
            <strong>97% of homebuyers</strong> now begin their property search
            online. Of those,
            <strong>74% say they would not visit a property</strong> without
            first seeing a virtual tour or 360 photos. Properties with 360°
            content receive <strong>403% more inquiry calls</strong> than
            listings with photos only. The implication is clear: in a market
            where buyers pre-qualify properties digitally before committing to
            an in-person visit, 360 photography is not a differentiator — it's
            the baseline expectation.
          </p>

          <h3>What 360 Photography Delivers That Standard Photos Cannot</h3>
          <p>
            Standard real estate photography shows selected angles chosen by the
            photographer to make the property look as appealing as possible.
            This is valuable, but buyers know it.
            <strong
              >360 photography shows buyers the complete spatial reality</strong
            >
            — room proportions, ceiling heights, natural light angles, traffic
            flow between rooms, and the relationship between spaces. This
            transparency actually
            <strong>increases buyer confidence</strong> rather than exposing
            flaws, because it eliminates the anxiety of "what am I not seeing?"
          </p>
        </section>

        <section>
          <h2>Equipment for 360 Real Estate Photography</h2>

          <h3>Smartphone + AI App (Recommended Starting Point)</h3>
          <p>
            A modern smartphone paired with a 360 capture app like Travvir is
            the most accessible entry point.
            <strong>Total setup cost: $0–$60/year.</strong> Modern flagship
            phones (iPhone 14 Pro+, Pixel 8 Pro, Galaxy S24 Ultra) capture
            sufficient detail for compelling real estate tours. The AI stitching
            in smartphone apps has reached a level where
            <strong
              >most buyers cannot distinguish the output from dedicated camera
              hardware</strong
            >
            in standard viewing conditions.
          </p>

          <h3>Dedicated 360 Cameras</h3>
          <p>
            <strong>Ricoh Theta Z1 ($999):</strong> The most popular dedicated
            360 camera among professional real estate photographers. Produces
            23MP images with excellent dynamic range for interior/exterior
            blends. <strong>Insta360 X3 ($449):</strong>
            Excellent entry-level dedicated camera with strong video
            capabilities.
            <strong>Matterport Pro3 ($5,995):</strong> The professional standard
            for luxury real estate requiring 3D spatial scanning.
          </p>

          <h3>Essential Accessories</h3>
          <p>
            <strong>Monopod or tripod:</strong> A $20–$50 monopod is sufficient
            for most residential shoots. For vacant properties where the tripod
            will appear in the nadir (floor shot), a tripod with a removable
            centre column is ideal.
            <strong>Remote shutter release:</strong> Eliminates camera shake
            from touching the screen. A $10 Bluetooth remote works with both
            smartphones and most dedicated cameras.
            <strong>Lens cleaning kit:</strong> Essential. A clean lens is
            non-negotiable in 360 photography because every fingerprint is
            visible.
          </p>
        </section>

        <section>
          <h2>Before You Shoot: Property Preparation</h2>

          <h3>The Pre-Shoot Checklist</h3>
          <p>
            <strong>Remove personal items:</strong> Family photos, personal
            memorabilia, prescription bottles on countertops — anything that
            makes the space feel occupied rather than available.
            <strong>Clear surfaces:</strong> Kitchen countertops should have
            only 1–2 decorative items maximum. Bathroom counters should be
            completely clear. <strong>Clean thoroughly:</strong> Mirrors,
            windows, and glass surfaces show reflections and smears in 360
            photography that are invisible in standard photos.
          </p>
          <p>
            <strong>Stage lighting:</strong> Turn on every light in the
            property. Open all window blinds to the same angle (typically 45°).
            This equalises light across rooms and prevents the dark-room vs
            bright-room contrast that causes exposure problems.
            <strong>Remove vehicles from driveway</strong> for exterior scenes.
            <strong>Mow lawn and edge sidewalks</strong> if season permits.
          </p>

          <h3>Timing Your Shoot</h3>
          <p>
            The best time to shoot interior 360 photography is
            <strong>mid-morning to early afternoon</strong> on overcast days.
            Overcast skies act as a giant softbox, eliminating harsh shadows and
            reducing window overexposure. Direct sunlight creates bright patches
            and deep shadows within the same scene, challenging even the best
            HDR processing.
          </p>
          <p>
            For exterior 360 scenes, <strong>golden hour</strong> (1 hour after
            sunrise or before sunset) produces the most visually appealing
            light. However, golden hour lighting inside creates warm-cast
            interiors that may look unnatural. Consider shooting interiors and
            exteriors at different times.
          </p>
        </section>

        <section>
          <h2>Room-by-Room Shooting Strategy</h2>

          <h3>Living Room and Main Living Areas</h3>
          <p>
            Position the camera at the
            <strong>natural entry point of the room</strong> — where a person
            would stand when first entering. For open-plan spaces, capture two
            scenes: one from the entry and one from the far end, to convey the
            full length of the space. Place the camera at
            <strong>chest height (1.2–1.4m)</strong>
            for the most natural perspective — not floor level, not eye level.
          </p>
          <p>
            <strong>Key shots for living rooms:</strong> Main seating area,
            fireplace focal point if present, view through to kitchen or dining
            area, window views.
          </p>

          <h3>Kitchen</h3>
          <p>
            The kitchen is the highest-value room in most residential
            properties. Position the camera in the
            <strong>centre of the kitchen triangle</strong> (sink, stove,
            refrigerator) to capture all three key elements in a single scene.
            For galley kitchens, shoot from one end looking down to convey the
            full length.
          </p>
          <p>
            <strong>Stage for maximum impact:</strong> A fruit bowl, fresh
            flowers, or a coffee machine on the counter. Nothing else. Clean
            every reflective surface (stainless steel appliances, granite
            countertops) immediately before shooting — dust and water spots are
            magnified in 360.
          </p>

          <h3>Master Bedroom</h3>
          <p>
            Position the camera at the
            <strong>foot of the bed, centred</strong>, looking toward the
            headboard. This is the perspective buyers immediately visualise when
            imagining themselves in the space. Include a second scene from the
            doorway to show room size context and flow to the ensuite.
          </p>

          <h3>Bathrooms</h3>
          <p>
            Bathrooms are challenging in 360 photography due to reflective
            surfaces and small dimensions. Use
            <strong>HDR capture mode</strong> if available — the mirror
            reflections and window light create extreme contrast. Position the
            camera at the doorway threshold rather than inside the bathroom,
            which conveys the full space without distortion from being too close
            to walls.
          </p>

          <h3>Exterior</h3>
          <p>
            Capture the exterior from the
            <strong>street or entry gate</strong> — the first view buyers see
            when arriving. Include the driveway, facade, and any significant
            landscaping features. For properties with outdoor entertaining
            areas, gardens, or pools, capture these as separate scenes within
            the tour navigation.
          </p>
        </section>

        <section>
          <h2>Technical Settings for Best Results</h2>

          <h3>Exposure Settings</h3>
          <p>
            <strong>If using a smartphone app:</strong> Enable HDR capture if
            offered. Lock exposure to the mid-tone of the room before starting
            capture. Avoid auto-exposure — it will produce different brightness
            levels in each frame, causing visible seams after stitching.
          </p>
          <p>
            <strong>If using a dedicated 360 camera:</strong> Shoot in HDR mode
            (3–5 bracket exposures). ISO 100–400 depending on available light.
            Shutter speed 1/30s or faster to avoid motion blur. White balance
            set to auto for consistency across rooms.
          </p>

          <h3>Camera Height and Angle</h3>
          <p>
            The optimal camera height for real estate 360 photography is
            <strong>1.2 metres (approximately 4 feet)</strong>. This is slightly
            below average eye level, which creates a perspective that makes
            rooms feel slightly larger and more welcoming than eye-level shots.
            Maintain this height consistently across all scenes — inconsistent
            heights between scenes create a disorienting experience in the tour.
          </p>
        </section>

        <section>
          <h2>Processing and Publishing Your Real Estate 360 Tour</h2>

          <h3>Post-Processing Best Practices</h3>
          <p>
            After capture, review each 360 scene at full resolution before
            publishing. Check the nadir (floor area directly below the camera) —
            this is often poorly covered and may show the tripod or your feet.
            Most apps fill this with a branded logo patch; ensure this looks
            clean.
          </p>
          <p>
            <strong>Colour correction:</strong> Adjust white balance and
            exposure globally rather than per-scene if possible. Consistent
            colour temperature across all rooms creates a more professional,
            cohesive tour experience.
          </p>

          <h3>Optimising for MLS and Property Portals</h3>
          <p>
            Most MLS systems have a dedicated "Virtual Tour URL" field. Paste
            your hosted tour link there. On Zillow, use the Virtual Tour section
            in the listing editor. On Realtor.com, add the tour URL in the media
            upload section. For personal website or agency CMS embedding, use
            the iframe embed code provided by your hosting platform.
          </p>
          <p>
            For step-by-step listing integration, see our guide on
            <a href="/blog/en/add-virtual-tour-to-listing"
              >how to add a virtual tour to your property listing</a
            >.
          </p>
        </section>

        <section>
          <h2>Common 360 Real Estate Photography Mistakes</h2>

          <h3>The Top 5 Errors That Ruin Real Estate Tours</h3>
          <p>
            <strong>1. Leaving mirrors partially exposed:</strong> Mirror
            reflections show the camera and photographer in the 360 image.
            Either position the camera to avoid direct mirror inclusion or use
            post-processing to remove the reflection.
          </p>
          <p>
            <strong>2. Shooting in direct sunlight:</strong> Creates overexposed
            windows and dark room interiors simultaneously. Schedule shoots for
            overcast conditions or use HDR capture.
          </p>
          <p>
            <strong>3. Including too many scenes:</strong> More than 15–20
            scenes in a single property tour causes buyers to lose interest and
            stop exploring. Be selective — every scene should add new
            information or perspective.
          </p>
          <p>
            <strong>4. Inconsistent camera height:</strong> Varying height
            between rooms creates a disorienting experience. Use a fixed-height
            monopod.
          </p>
          <p>
            <strong>5. Not testing on mobile:</strong> Most buyers view tours on
            smartphones. Always preview and test your tour on a mobile device
            before publishing.
          </p>
        </section>

        <section>
          <h2>Conclusion: Building a 360 Photography Practice</h2>
          <p>
            360 real estate photography is a learnable skill that improves
            rapidly with practice. Your first 2–3 tours will take longer and
            produce less polished results than your 10th tour — and that's
            expected. The investment of time and the occasional early learning
            curve pays off significantly in client satisfaction, listing
            competitiveness, and professional reputation.
          </p>
          <p>
            Start with your next vacant listing. Travvir offers a free trial —
            download the Travvir app to start your free trial, create a complete
            property tour, share it with the seller, and
            observe their reaction. In our experience,
            <strong
              >sellers who see their property in a quality 360 tour become
              enthusiastic advocates</strong
            >
            for the technology — and enthusiastic advocates give you referrals.
          </p>
        </section>
        <section>
          <h2>Continue Reading</h2>
          <ul>
          <li><a href="/blog/en/how-to-make-virtual-tour-real-estate">Make a Real Estate Virtual Tour</a></li>
          <li><a href="/blog/en/interior-photography-360-virtual-tour">Interior Photography for 360 Tours</a></li>
          <li><a href="/blog/en/how-to-take-360-photos-smartphone">Take 360 Photos with Your Phone</a></li>
          <li><a href="/blog/en/fix-blurry-360-photos">Fix Blurry 360 Photos</a></li>
          <li><a href="/blog/en/360-camera-vs-dslr-real-estate">360 Camera vs DSLR for Real Estate</a></li>
          <li><a href="/blog/en/real-estate-virtual-tour-smartphone">Real Estate Tours by Phone</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "360-real-estate-photography-guide",
);

useBlogSeo({
  title: "360 Real Estate Photography: Agent's Guide [2026] | Travvir",
  description:
    "Complete guide to 360 real estate photography for agents. Learn equipment, lighting, room-by-room technique, and how to deliver virtual tours that sell.",
  keywords:
    "360 real estate photography, 360 home tour, real estate 360 photos, 360 virtual tour real estate guide, real estate photography tips",
  slug: "360-real-estate-photography-guide",
  image: postData?.image,
  postData,
  lang: "en",
});

</script>
